<template>
  <span class="tapp-logo">
    <!-- eslint-disable-next-line vue/no-v-html -->
    <span class="tapp-logo__inner" :style="{ width, height }" v-html="logo"></span>
  </span>
</template>

<script setup lang="ts">
  const props = defineProps<{
    /**
     * `brandCode` specifies the identifier for the brand whose logo is to be displayed.
     * This code corresponds to a specific logo file within the `./logos` directory.
     * For example, setting `brandCode` to 'dpg' would attempt to load the logo from a file named `logo-dpg.svg`.
     * If `brandCode` is not provided, a default logo specified in the component will be used.
     */
    brandCode?: string;

    /**
     * `width` sets the width of the logo. This value can be any valid CSS size unit (e.g., '50px', '4rem').
     * If not specified, the logo will inherit the width of its container, allowing for flexible layout options.
     */
    width?: string;

    /**
     * `height` sets the height of the logo. Like `width`, this can be any valid CSS size unit.
     * If not provided, the logo will scale according to its aspect ratio to fill the available height,
     * maintaining its intrinsic proportions.
     */
    height?: string;
  }>();

  const logo = ref('');

  const logos = Object.fromEntries(
    Object.entries(import.meta.glob('./logos/*.svg', { query: '?raw', import: 'default' })).map(([key, value]) => {
      const filename = key.split('/').pop()!.split('.').shift();
      return [filename, value];
    }),
  );

  async function initLogo() {
    const logoName = props.brandCode ? `logo-${props.brandCode}` : 'logo-dpg';

    if (logos && logoName && typeof logos[logoName] === 'function') {
      logo.value = await logos[logoName]();
    }
  }

  /*
   * Call initLogo once to load the right logo on the server
   */
  await initLogo();
</script>

<style lang="postcss" scoped>
  .tapp-logo {
    display: block;
    width: 100%;
    height: 100%;

    &__inner {
      width: inherit;
      height: inherit;

      :deep() svg,
      :deep() img {
        display: block;
        width: inherit;
        height: inherit;
      }

      :deep() svg {
        fill: currentcolor;
      }
    }
  }
</style>
